import React, { Component } from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';
import get from 'lodash/get';

import LineGraph from 'components/Charts/LineGraph';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Select from 'components/Select';
import ExportButton from 'components/Export';
import Spinner from 'components/Spinner';

import { useUserMe } from 'apiTyped/hooks/users.query';

const Container = styled.div`
  position: relative;
  display: grid;

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .rightComponentsContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    > :not(:last-child) {
      margin-right: 15px;
    }

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      margin-top: 40px;
    }
  }

  .selectSetsContainer {
    width: 90%;
    margin: 4% auto 0 auto;

    @media (max-width: 845px) {
      width: 100%;
    }
  }

  .setListContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    margin-top: 5px;

    > div {
      margin: 0 !important;
      height: 30px !important;
      white-space: nowrap !important;
      width: initial !important;
    }

    .tag {
      width: initial !important;
    }

    > div:not(:last-child) {
      margin-right: 10px !important;
    }
  }

  .compareHistoryChartContainer {
    margin-top: 40px;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      margin-left: -35px;
      margin-top: 0;
    }
  }

  .ttPillsContainer {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .labelSelect {
    font-size: 14px;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      font-size: 12px;
    }
  }

  .card-header {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 20px;

    @media (max-width: 845px) {
      display: block;
      gap: 16px;
    }
  }

  .historique-primeur {
    @media (max-width: 845px) {
      width: calc(100% - 32px);
    }
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    > div {
      padding: 16px;
      width: calc(100vw - 64px);
      height: calc(100% - 32px);
    }
  }
`;

class CompareHistoryLineChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tagList: [],
      forceCloseMenu: false
    };
  }

  getOptions() {
    const data = this.props.data || [];
    if (!data.length) {
      return [];
    }
    const { isPrimeur, clientTags } = this.props;
    let options = [];
    // If we have WxR enabled, we want this as the first option in the dropdown:
    if (clientTags.includes('Bordeaux')) {
      isPrimeur
        ? options.push({
            value: 'wxr_primeur',
            label: 'WXR',
            order: 0
          })
        : options.push({
            value: 'wxr_note',
            label: 'WXR',
            order: 0
          });
    }
    data.forEach((wine) => {
      Object.keys(wine).forEach((key) => {
        let optionsNames = options.map((item) => item.label);
        if (
          key.includes('_allnotes') &&
          wine[key].find((item) => item.normalized_note_filtered != '-') &&
          !optionsNames.includes(wine[key][0].journalist_name)
        ) {
          const hasLivrable = wine[key].filter(
            (item) => item.note_type != 'Primeur'
          );
          if (isPrimeur || hasLivrable.length > 0) {
            options.push({
              value: wine[key][0].journalist_short_name,
              label: wine[key][0].journalist_name,
              order: wine[key][0].order_display
            });
          }
        }
      });
    });

    options = options.sort(function (a, b) {
      return Number(a.order) - Number(b.order);
    });

    return options;
  }

  renderCardHeader(options) {
    const { title, taster, onChangeTaster, exportName } = this.props;
    return (
      <CardHeader
        title={
          <div className="card-header">
            <h1 className="ttTitle">{title}</h1>
            <div className="ttPillsContainer ignored-export">
              <p className="labelSelect">
                {I18n.t('notes.compare.journalistSelectLabel')} :{' '}
              </p>
              <Select
                placeholder="James Suckling"
                value={taster}
                minWidth="170px"
                onChange={(value) => onChangeTaster(value)}
                options={options}
                dropdownProps={{
                  styles: `margin-left:-25px`
                }}
              />
            </div>
          </div>
        }
        fontSize="20px"
        iconRight
        rightComponents={
          <div className="rightComponentsContainer ignored-export">
            <ExportButton
              jpg
              pdf
              name={exportName + title}
              targetElement="#historique-primeur"
              colors={['#e25d34', '#1f2b37', '#a5aaaf', '#B1B7BD', '#C9D0D6']}
            />
          </div>
        }
      />
    );
  }

  renderSelectSets() {
    const { rivals } = this.props;
    const options =
      rivals && rivals.length > 0
        ? [...new Set(rivals.map((item) => item.wine_name))].map((item) => ({
            value: item,
            label: item
          }))
        : [];

    return (
      <div className="selectSetsContainer ignored-export">
        <Select
          placeholder={I18n.t('placeholderRival')}
          minWidth="170px"
          isMulti
          value={this.state.tagList}
          dropdownProps={{
            styles: `margin-left:20px;margin-right:20px;`
          }}
          onChange={(value) =>
            this.setState({
              tagList:
                value && value.length > 5 ? value.slice(1, value.length) : value
            })
          }
          options={options}
        />
      </div>
    );
  }

  formatSet(data, options) {
    const { isPrimeur, taster } = this.props;
    let formattedData = [];
    if (data.length > 0) {
      data.forEach((wine) => {
        if (
          !taster ||
          taster.value == 'wxr_note' ||
          taster.value == 'wxr_primeur'
        ) {
          if (isPrimeur) {
            formattedData.push({
              value: wine.wxr_primeur,
              raw: wine.wxr_primeur,
              name: wine.vintage,
              wine: wine.wine_name
            });
          } else {
            formattedData.push(
              {
                value: wine.wxr_primeur,
                raw: wine.wxr_primeur,
                name: 'Primeur',
                wine: wine.wine_name
              },
              {
                value: wine.wxr_note,
                raw: wine.wxr_note,
                name: wine.vintage,
                wine: wine.wine_name
              }
            );
          }
        } else {
          if (isPrimeur) {
            let value = 0;
            let raw = 0;
            if (taster) {
              if (
                taster.value == 'CK' ||
                taster.value == 'JMQ' ||
                taster.value == 'RVF'
              ) {
                value = wine[taster.value + '_primeur'];
              } else {
                value = wine[taster.value + '_rawPrimeurFloat'];
              }
              raw = wine[taster.value + '_rawPrimeur'];
            }
            if (!value || isNaN(value)) {
              value = 0;
              raw = 0;
            }
            if (value != 0) {
              formattedData.push({
                value,
                raw,
                name: wine.vintage,
                wine: wine.wine_name
              });
            }
          } else {
            let allnotes = [];
            if (taster) {
              allnotes = wine[taster.value + '_allnotes'];
            }
            if (!allnotes) {
              allnotes = [];
            }
            if (
              (!allnotes || !allnotes.length) &&
              options &&
              options.length > 0
            ) {
              allnotes = wine[options[0].value + '_allnotes'];
            }
            if (!allnotes) {
              allnotes = [];
            }
            allnotes.forEach((note) => {
              const name =
                note.note_type === 'Primeur'
                  ? 'Primeur'
                  : moment(note.date).format('YYYY');
              let value =
                note.journalist_short_name === 'CK' ||
                note.journalist_short_name === 'JMQ' ||
                note.journalist_short_name === 'RVF'
                  ? note.normalized_note_filtered
                  : note.float_note_filtered;
              let raw = note.note_filtered;
              if (!value || isNaN(value)) {
                value = 0;
              }
              if (value != 0) {
                formattedData.push({
                  name,
                  value,
                  wine: note.wine_name,
                  raw,
                  date: note.date
                });
              }
            });
          }
        }
      });
      if (!isPrimeur) {
        formattedData = formattedData.sort(function (a, b) {
          return String(a.name).localeCompare(String(b.name));
        });
        const lastItem = formattedData.pop();
        if (lastItem && lastItem.name === 'Primeur') {
          formattedData.unshift(lastItem);
        } else if (lastItem) {
          formattedData.push(lastItem);
        }
      }
    }

    return formattedData;
  }

  formatData(options) {
    const isPrimeur = this.props.isPrimeur;
    const taster = this.props.taster;

    const isWxrLivrable =
      !isPrimeur &&
      (!taster || taster.value == 'wxr_note' || taster.value == 'wxr_primeur');

    const data = isWxrLivrable
      ? this.props.dataWxr || []
      : this.props.data || [];

    let rivals = isWxrLivrable
      ? this.props.rivalsWxr || []
      : this.props.rivals || [];

    let { tagList } = this.state;
    tagList = tagList.map((item) => item.value);
    let formattedData = [data];

    let rivalsSet = [];
    tagList.forEach((tag) => {
      const set = rivals.filter((item) => item.wine_name === tag);
      rivalsSet.push(set);
    });
    formattedData.push(...rivalsSet);
    if (isWxrLivrable) {
      formattedData = formattedData.map((set) => this.formatSetLivrables(set));
    } else {
      formattedData = formattedData.map((set) => this.formatSet(set, options));
    }

    return formattedData;
  }

  formatSetLivrables(data) {
    const setData = [...data];
    return setData.map((item) => ({
      ...item,
      name: item.year,
      value: !isNaN(item.wxr) ? Number(item.wxr).toFixed(2) : 0,
      raw: !isNaN(item.wxr) ? Number(item.wxr).toFixed(2) : 0,
      wine: item.wine_name
    }));
  }

  getDomain(data) {
    const isUnder20 =
      get(data, '[0][0].value') && get(data, '[0][0].value') < 30;
    if (isUnder20) {
      return {
        domain: [10, 20],
        ticks: [10, 12, 14, 16, 18, 20]
      };
    }
    return {
      domain: [80, 100],
      ticks: [80, 85, 90, 95, 100]
    };
  }

  clearSameYear(data) {
    return data.map((set) =>
      set.filter((item) => {
        const alreadyExist = set.find(
          (findItem) =>
            findItem.name === item.name && findItem.date !== item.date
        );
        if (
          alreadyExist &&
          moment(item.date).isBefore(moment(alreadyExist.date))
        ) {
          return false;
        }
        return true;
      })
    );
  }

  render() {
    const options = this.getOptions();
    let formattedData = this.formatData(options);

    let labels = [];
    formattedData = this.clearSameYear(formattedData);
    if (formattedData && formattedData.length > 0) {
      formattedData.forEach((set) => {
        if (set.length > 0) {
          labels.push(set[0].wine);
        }
      });
    }
    const domain = this.getDomain(formattedData);

    return (
      <Container className="historique-primeur">
        <Card id="historique-primeur">
          {this.renderCardHeader(options)}
          <div className="compareHistoryChartContainer">
            <LineGraph
              height={300}
              data={formattedData}
              labels={labels}
              marginsChart={{ top: 20, right: 20 }}
              sortMethod={(a, b) => parseInt(a.name, 10) - parseInt(b.name, 10)}
              yAxisProps={{
                type: 'number',
                domain: domain.domain,
                ticks: domain.ticks,
                interval: 'preserveStartEnd'
              }}
              strokeDasharray={[null, ['5 5'], null, ['5 5'], null]}
              colors={[
                '#e25d34',
                '#1f2b37',
                '#273645',
                '#a5aaaf',
                '#B1B7BD',
                '#C9D0D6',
                '#b7bbbf',
                '#dbdddf',
                '#C4C8CC',
                '#D5D9DE',
                '##E1E6EB'
              ]}
            />
          </div>
          {this.renderSelectSets()}
        </Card>
        <Spinner loading={this.props.loading} little />
      </Container>
    );
  }
}

const Index = (props) => {
  const { user } = useUserMe();
  return <CompareHistoryLineChart {...props} clientTags={user.client.tags} />;
};

export default Index;
